import * as React from "react"
import { Eye, Pencil } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ListEditorSheet } from "@/components/alfred/list-editor-sheet"
import { CharacterCounter } from "@/components/shared/character-counter"
import { PROTECTED_CREDENTIAL_MASK } from "@/components/shared/masked-credential-input"
import {
  getAlfredMaxLengthError,
  MAX_ALFRED_INDEXED_STRING_LENGTH,
} from "@/lib/alfred-field-validation"
import type { GoogleVendor } from "@/types/alfred-config"

interface GoogleVendorEditorProps {
  items: GoogleVendor[]
  onChange: (next: GoogleVendor[]) => void
  disabled?: boolean
  triggerAriaLabel?: string
  requireSecret?: boolean
}

const INPUT_CLASSES =
  "bg-transparent border-input text-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px]"
const LABEL_CLASSES = "text-[13px] text-muted-foreground font-medium"

function emptyGoogleVendor(): GoogleVendor {
  return { collectorId: "", keyPath: "", keyVersion: 1, password: "", vendor: "" }
}

export function GoogleVendorEditor({
  items,
  onChange,
  disabled = false,
  triggerAriaLabel,
  requireSecret = false,
}: GoogleVendorEditorProps) {
  const [open, setOpen] = React.useState(false)

  return (
    <>
      <Button
        aria-label={triggerAriaLabel}
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="bg-card border-input text-foreground hover:bg-secondary"
      >
        {disabled ? <Eye className="h-4 w-4 mr-2" /> : <Pencil className="h-4 w-4 mr-2" />}
        {disabled ? "View" : "Manage"}
      </Button>
      <ListEditorSheet<GoogleVendor>
        open={open}
        onOpenChange={setOpen}
        title="Google vendors"
        description="Manage Google vendor credentials used by Alfred."
        itemNoun="credential"
        items={items}
        newItem={emptyGoogleVendor}
        readOnly={disabled}
        getValidationError={(draft, newItemIndexes) => {
          const longVendorIndex = draft.findIndex((item) =>
            getAlfredMaxLengthError("Vendor", item.vendor),
          )
          if (longVendorIndex >= 0) {
            return getAlfredMaxLengthError(
              `Vendor ${longVendorIndex + 1}`,
              draft[longVendorIndex].vendor,
            )
          }
          const missingPassword = draft.some((item, index) =>
            (requireSecret || newItemIndexes.has(index)) && !item.password?.trim(),
          )
          if (!missingPassword) return undefined
          return requireSecret
            ? "Enter a password for every Google vendor credential."
            : "Enter a password for every new Google vendor credential."
        }}
        renderForm={(item, update, _index, readOnly, isNew) => {
          const passwordRequired = requireSecret || isNew
          return (
          <>
            <div className="space-y-2">
              <Label className={LABEL_CLASSES}>Vendor</Label>
              <Input
                value={item.vendor}
                disabled={readOnly}
                onChange={(e) => update({ vendor: e.target.value })}
                placeholder="e.g. google"
                className={INPUT_CLASSES}
              />
              <CharacterCounter
                value={item.vendor}
                maxLength={MAX_ALFRED_INDEXED_STRING_LENGTH}
              />
            </div>
            <div className="space-y-2">
              <Label className={LABEL_CLASSES}>Collector ID</Label>
              <Input
                value={item.collectorId}
                disabled={readOnly}
                onChange={(e) => update({ collectorId: e.target.value })}
                placeholder="collector-1"
                className={INPUT_CLASSES}
              />
            </div>
            <div className="space-y-2">
              <Label className={LABEL_CLASSES}>Key path</Label>
              <Input
                value={item.keyPath}
                disabled={readOnly}
                onChange={(e) => update({ keyPath: e.target.value })}
                placeholder="/keys/google.pem"
                className={INPUT_CLASSES}
              />
            </div>
            <div className="space-y-2">
              <Label className={LABEL_CLASSES}>Key version</Label>
              <Input
                type="number"
                min={0}
                step={1}
                value={item.keyVersion}
                disabled={readOnly}
                onChange={(e) => {
                  const raw = e.target.value
                  if (raw === "") {
                    update({ keyVersion: 0 })
                    return
                  }
                  const parsed = Number(raw)
                  update({ keyVersion: Number.isNaN(parsed) ? 0 : parsed })
                }}
                className={INPUT_CLASSES}
              />
            </div>
            <div className="space-y-2">
              <Label className={LABEL_CLASSES}>Password</Label>
              <Input
                aria-label="Password"
                type="password"
                value={readOnly ? PROTECTED_CREDENTIAL_MASK : item.password}
                disabled={readOnly}
                onChange={(e) => update({ password: e.target.value })}
                placeholder={
                  readOnly
                    ? undefined
                    : passwordRequired
                      ? "Enter password"
                      : "Leave blank to keep current"
                }
                required={!readOnly && passwordRequired}
                aria-required={!readOnly && passwordRequired}
                autoComplete="new-password"
                className={`${INPUT_CLASSES} placeholder:text-muted-foreground`}
              />
              {!readOnly && (
                <p className="text-xs text-muted-foreground">
                  {passwordRequired
                    ? "A password is required for every new credential."
                    : "Leave blank to keep current. To change it, enter a new password."}
                </p>
              )}
            </div>
          </>
          )
        }}
        onSubmit={onChange}
      />
    </>
  )
}
